import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Mail } from 'lucide-react';

export function PrivacyPolicy() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <Badge variant="outline" className="mb-4">
            <Shield className="h-4 w-4 mr-2" />
            GDPR Compliant
          </Badge>
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="text-muted-foreground">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>1. Controller Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Data Controller:</h4>
              <p>TopMatchTalent.com</p>
              <div className="flex items-center gap-2 mt-2">
                <Mail className="h-4 w-4" />
                <span>talent@topmatchtalent.com</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Data Protection Officer (DPO):</h4>
              <p>Email: dpo@topmatchtalent.com</p>
              <p className="text-sm text-muted-foreground">
                For all data protection inquiries and exercising your rights
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>2. Data We Collect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Personal Data:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Account information (email, name, company)</li>
                <li>Candidate CVs and resumes uploaded by you</li>
                <li>Job descriptions and requirements</li>
                <li>Analysis results and AI-generated insights</li>
                <li>Usage data and analytics</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Technical Data:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP addresses and browser information</li>
                <li>Cookies and tracking technologies</li>
                <li>Session logs and performance metrics</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3. Legal Basis for Processing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">We process your data based on:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contract (Art. 6(1)(b) GDPR):</strong> To provide our recruitment services</li>
                <li><strong>Legitimate Interests (Art. 6(1)(f) GDPR):</strong> To improve our services and analytics</li>
                <li><strong>Consent (Art. 6(1)(a) GDPR):</strong> For marketing communications and optional features</li>
                <li><strong>Legal Obligation (Art. 6(1)(c) GDPR):</strong> For compliance and legal requirements</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4. Data Retention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Retention Periods:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Account Data:</strong> Until account deletion + 30 days</li>
                <li><strong>Candidate Data:</strong> 2 years from last access (unless extended by user)</li>
                <li><strong>Analysis Results:</strong> 2 years from creation</li>
                <li><strong>Technical Logs:</strong> 1 year maximum</li>
                <li><strong>Marketing Data:</strong> Until consent withdrawal</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5. Your Rights Under GDPR</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">You have the right to:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate data</li>
                <li><strong>Erasure:</strong> Delete your data ("right to be forgotten")</li>
                <li><strong>Portability:</strong> Export your data in structured format</li>
                <li><strong>Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Object:</strong> Opt out of certain processing activities</li>
                <li><strong>Withdraw Consent:</strong> Revoke consent at any time</li>
              </ul>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Exercise Your Rights:</h4>
              <p>Email us at: <strong>dpo@topmatchtalent.com</strong></p>
              <p className="text-sm text-muted-foreground mt-1">
                We will respond within 30 days as required by GDPR
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>6. Data Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Technical Safeguards:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>End-to-end encryption for data in transit and at rest</li>
                <li>Multi-factor authentication and access controls</li>
                <li>Regular security audits and penetration testing</li>
                <li>Automated backup and disaster recovery procedures</li>
                <li>Data minimization and privacy by design principles</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>7. International Transfers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p>
                Your data may be processed in the EU/EEA. For any transfers outside the EU/EEA, 
                we ensure adequate protection through:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Adequacy decisions by the European Commission</li>
                <li>Binding Corporate Rules where applicable</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>8. Contact & Complaints</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Data Protection Officer:</h4>
                  <p>dpo@topmatchtalent.com</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Supervisory Authority:</h4>
                  <p>You can file complaints with your local data protection authority</p>
                </div>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">For German Clients:</h4>
              <p>Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI)</p>
              <p className="text-sm">Graurheindorfer Straße 153, 53117 Bonn</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 